import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Search, LayoutGrid, ShoppingBag, Building2, MousePointer2, X, Eye, ArrowRight, Briefcase, Users } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { API_URLS, API_CONFIG } from '../../config/api';
import { formatPrice as formatPriceUtil, normalizeUrl } from '../../utils/format';

// Aynı hover scroll davranışı (diğer tasarım sayfalarıyla uyumlu)
const customStyles = `
  .group:hover .group-hover\\:object-bottom {
    transition: all 20s ease;
  }
  .image-short { transition: all 3s ease !important; }
  .image-medium { transition: all 8s ease !important; }
  .image-long { transition: all 15s ease !important; }
  .image-very-long { transition: all 25s ease !important; }
`;

function getImageTransitionClass(naturalHeight) {
  if (!naturalHeight) return '';
  if (naturalHeight < 800) return 'image-short';
  if (naturalHeight < 1500) return 'image-medium';
  if (naturalHeight < 2500) return 'image-long';
  return 'image-very-long';
}

const TYPE_CONFIG = {
  all: {
    label: 'Tüm Tasarımlar',
    icon: LayoutGrid,
  },
  ecommerce: {
    label: 'E-ticaret Tasarımları',
    icon: ShoppingBag,
  },
  professional_ecommerce: {
    label: 'Profesyonel E-Ticaret Tasarımları',
    icon: Briefcase,
  },
  b2b: {
    label: 'B2B Tasarımları',
    icon: Users,
  },
  corporate: {
    label: 'Kurumsal Site Tasarımları',
    icon: Building2,
  },
  easy: {
    label: 'Kolay Site Tasarımları',
    icon: MousePointer2,
  },
};

// Basit, Türkçe uyumlu normalizasyon (elastik arama için)
function normalizeText(value) {
  if (!value) return '';
  return String(value)
    .toLowerCase()
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ı/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/İ/g, 'i')
    .replace(/Ğ/g, 'g')
    .replace(/Ü/g, 'u')
    .replace(/Ş/g, 's')
    .replace(/Ö/g, 'o')
    .replace(/Ç/g, 'c');
}

// Türkçe uyumlu, URL-friendly slug üretici (kategoriler için)
function slugify(value) {
  const norm = normalizeText(value || '')
    .replace(/&/g, ' ve ')
    .replace(/[^a-z0-9\s-]/g, ' ')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return norm;
}

// Eski/bozuk slug'ları bilinen üst kategori isimlerine eşlemek için tablo
const LEGACY_CATEGORY_SLUG_MAP = {
  'end-stri-1': 'Endüstri',
  'endustri-1': 'Endüstri',
  'endustri': 'Endüstri',
  'magaza-1': 'Mağaza',
  'magaza': 'Mağaza',
  'is-dunyasi-hizmetler-1': 'İş Dünyası & Hizmetler',
  'is-dunyasi-hizmetler': 'İş Dünyası & Hizmetler',
  'topluluk-1': 'Topluluk',
  'topluluk': 'Topluluk',
  'blog-1': 'Blog',
  'blog': 'Blog',
  'yemek-ve-restoran-1': 'Yemek ve Restoran',
  'yemek-ve-restoran': 'Yemek ve Restoran',
};

function normalizeImageUrl(url) {
  const BASE_URL = API_CONFIG.BASE_URL;
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/admin_api.php')) {
    const m = url.match(/getScreenshot&file=([^&]+)$/);
    if (m && m[1]) return `${BASE_URL}/backend/uploads/${m[1]}`;
    return `${BASE_URL}/backend${url}`;
  }
  return `${BASE_URL}${url}`;
}

function getLimitedCategories(item, max = 2) {
  const names = [];

  if (item.category_names && Array.isArray(item.category_names) && item.category_names.length > 0) {
    names.push(...item.category_names);
  }

  if (names.length === 0 && item.category) {
    names.push(item.category);
  }

  return names.slice(0, max);
}

function getLimitedTags(item, max = 3) {
  if (!item.tags) return [];
  let tags = [];

  if (typeof item.tags === 'string') {
    try {
      tags = JSON.parse(item.tags);
    } catch {
      tags = item.tags.split(',').map(t => t.trim());
    }
  } else if (Array.isArray(item.tags)) {
    tags = item.tags;
  }

  return tags.slice(0, max);
}


function TemplateCard({ template }) {
  const navigate = useNavigate();
  const { type } = template;

  const typeConfig = TYPE_CONFIG[type] || TYPE_CONFIG.all;
  const TypeIcon = typeConfig.icon || LayoutGrid;

  const getDetailUrl = () => {
    // Tasarım tipine göre detay sayfası URL'ini döndür
    // Her zaman slug kullan (slug yoksa veya tpl_ ile başlıyorsa id kullan)
    if (!template.id && !template.slug) return null;
    
    // Slug varsa ve tpl_ ile başlamıyorsa slug kullan, yoksa id kullan
    const identifier = (template.slug && !template.slug.startsWith('tpl_')) 
      ? template.slug 
      : (template.id || template.slug);
    
    if (!identifier) return null;
    
    if (type === 'ecommerce') {
      return `/eticaret-tasarimlari/${encodeURIComponent(identifier)}`;
    }
    
    if (type === 'corporate') {
      return `/kurumsal-web-sitesi-tasarimlari/${encodeURIComponent(identifier)}`;
    }
    
    // Kolay site tasarımları (easy) için: slug kullan (slug yoksa veya tpl_ ile başlıyorsa id kullan)
    return `/hazir-web-sitesi-tasarimlari/${encodeURIComponent(identifier)}`;
  };

  const handlePrimaryAction = () => {
    // Detay sayfası olanlar için route'a yönlendir, diğerleri için demo bağlantısı aç
    const detailUrl = getDetailUrl();
    if (detailUrl) {
      navigate(detailUrl);
      return;
    }
    if (template.demo_url) {
      const normalizedUrl = normalizeUrl(template.demo_url);
      window.open(normalizedUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const categories = getLimitedCategories(template);
  const tags = getLimitedTags(template);

  const hasDemo = !!template.demo_url;
  const hasAdmin = !!template.admin_url;
  const hasPurchase = !!template.purchase_url;

  const priceValue =
    template.price !== undefined && template.price !== null
      ? template.price
      : template.sale_price !== undefined && template.sale_price !== null
      ? template.sale_price
      : null;

  const formattedPrice =
    priceValue !== null && priceValue !== ''
      ? formatPriceUtil(priceValue, '₺', 0, 0)
      : null;

  return (
    <Card className="group overflow-hidden border border-gray-200 hover:border-purple-400 hover:shadow-xl transition-all duration-300 bg-white flex flex-col">
      <div className="relative aspect-[16/10] bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
        {template.image_url || template.image ? (
          <img
            src={normalizeImageUrl(template.image_url || template.image)}
            alt={template.name}
            loading="lazy"
            className="w-full h-full object-cover object-top transition-all duration-[20s] ease group-hover:object-bottom"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
            onLoad={(e) => {
              const nh = e.target.naturalHeight;
              const cls = getImageTransitionClass(nh);
              if (cls) e.target.classList.add(cls);
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
            Önizleme görseli henüz eklenmedi
          </div>
        )}

        <div className="absolute top-3 left-3 flex items-center gap-2">
          <Badge className="bg-white/90 text-gray-800 border border-gray-200 text-[10px] font-semibold uppercase tracking-wide">
            <TypeIcon className="w-3 h-3 mr-1" />
            {typeConfig.label}
          </Badge>
        </div>
      </div>

      <CardContent className="flex-1 flex flex-col p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="text-sm sm:text-base font-semibold text-gray-900 line-clamp-2">
            {template.name}
          </h3>
        </div>

        {template.description && (
          <p className="text-xs sm:text-sm text-gray-600 line-clamp-2 mb-3">
            {template.description}
          </p>
        )}

        {(categories.length > 0 || tags.length > 0) && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {categories.map((cat, idx) => (
              <Badge
                key={`cat-${idx}`}
                variant="outline"
                className="border-purple-200 bg-purple-50 text-[10px] sm:text-xs text-purple-700"
              >
                {cat}
              </Badge>
            ))}
            {tags.map((tag, idx) => (
              <Badge
                key={`tag-${idx}`}
                variant="outline"
                className="border-gray-200 bg-gray-50 text-[10px] sm:text-xs text-gray-700"
              >
                {tag}
              </Badge>
            ))}
          </div>
        )}

        <div className="mt-auto pt-2 space-y-3">
          {/* Tasarımı İncele Button - Always visible if slug or id exists (especially for kolay site tasarımları) */}
          {getDetailUrl() && (
            <Link to={getDetailUrl()} className="block">
              <Button
                size="sm"
                variant="outline"
                className="w-full font-semibold transition-all duration-300 transform hover:scale-105 bg-white hover:bg-gray-50 border-gray-300 text-gray-700"
              >
                <Eye className="w-4 h-4 mr-2" />
                <span>Tasarımı İncele</span>
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          )}

          {/* Action buttons - Admin and Purchase */}
          {(hasAdmin || hasPurchase) && (
            <div className="flex flex-wrap gap-2 justify-between">
              {hasAdmin && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="flex-1 text-xs sm:text-sm bg-white hover:bg-gray-50 border-gray-300 text-gray-700"
                  onClick={() => window.open(template.admin_url, '_blank', 'noopener,noreferrer')}
                >
                  Admin
                </Button>
              )}
              {hasPurchase && (
                <Button
                  type="button"
                  size="sm"
                  className="flex-1 text-xs sm:text-sm bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={() => window.open(template.purchase_url, '_blank', 'noopener,noreferrer')}
                >
                  Satın Al
                </Button>
              )}
            </div>
          )}

          {/* Demo button - separate if exists */}
          {hasDemo && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full text-xs sm:text-sm bg-white hover:bg-gray-50 border-gray-300 text-gray-700"
              onClick={() => {
                const normalizedUrl = normalizeUrl(template.demo_url);
                window.open(normalizedUrl, '_blank', 'noopener,noreferrer');
              }}
            >
              Demo
            </Button>
          )}

          {/* Price bottom centered */}
          {formattedPrice && (
            <div className="pt-1 text-center">
              <div className="text-sm sm:text-base font-semibold text-gray-900">
                {formattedPrice}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function WebDesignTemplatesPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeType, setActiveType] = useState('all');
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const [hoveredRootId, setHoveredRootId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ecomCategories, setEcomCategories] = useState([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedMobileRootId, setExpandedMobileRootId] = useState(null);
  const [isUrlParamsProcessed, setIsUrlParamsProcessed] = useState(false);
  const lastProcessedUrlRef = useRef('');

  // Kategori ağacı (3 seviye)
  const categoryTree = useMemo(() => {
    if (!Array.isArray(ecomCategories) || ecomCategories.length === 0) return [];

    const map = {};
    const roots = [];

    ecomCategories.forEach((cat) => {
      map[cat.id] = { ...cat, children: [] };
    });

    ecomCategories.forEach((cat) => {
      if (cat.parent_id && map[cat.parent_id]) {
        map[cat.parent_id].children.push(map[cat.id]);
      } else {
        roots.push(map[cat.id]);
      }
    });

    // Sadece en az bir alt kategorisi olan üst kategorileri göster
    return roots.filter((root) => root.children && root.children.length > 0);
  }, [ecomCategories]);

  // Bir kategorinin kendisi + tüm altlarını döndür (filtre için)
  const getCategoryAndDescendants = (categoryId) => {
    if (!categoryId) return [];
    const result = [categoryId];

    const walk = (parentId) => {
      ecomCategories.forEach((cat) => {
        if (cat.parent_id === parentId) {
          if (!result.includes(cat.id)) {
            result.push(cat.id);
            walk(cat.id);
          }
        }
      });
    };

    walk(categoryId);
    return result;
  };

  // Aktif alt kategoriye göre hangi üst kategorinin seçili olduğunu bul
  const activeRootId = useMemo(() => {
    if (!activeCategoryId || !Array.isArray(categoryTree)) return null;
    for (const root of categoryTree) {
      // Eğer doğrudan üst kategori seçildiyse
      if (root.id === activeCategoryId) {
        return root.id;
      }
      for (const mid of root.children || []) {
        for (const leaf of mid.children || []) {
          if (leaf.id === activeCategoryId) {
            return root.id;
          }
        }
      }
    }
    return null;
  }, [activeCategoryId, categoryTree]);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);

        const params = 'limit=60&offset=0';
        const [ecomRes, corpRes, easyRes, ecomCatRes] = await Promise.all([
          fetch(`${API_URLS.ECOMMERCE_DESIGNS}&${params}`),
          fetch(`${API_URLS.CORPORATE_DESIGNS}&${params}`),
          fetch(API_URLS.ADMIN.WEBSITE_TEMPLATES),
          fetch(API_URLS.ECOMMERCE_DESIGN_CATEGORIES),
        ]);

        const [ecomJson, corpJson, easyJson, ecomCatJson] = await Promise.all([
          ecomRes.json(),
          corpRes.json(),
          easyRes.json(),
          ecomCatRes.json(),
        ]);

        const ecomList = Array.isArray(ecomJson?.data) ? ecomJson.data : [];
        const corpList = Array.isArray(corpJson?.data) ? corpJson.data : [];
        const easyList = Array.isArray(easyJson) ? easyJson : (easyJson?.data || []);

        // E-ticaret kategori ağacını al
        const ecomCats = Array.isArray(ecomCatJson?.data)
          ? ecomCatJson.data
          : Array.isArray(ecomCatJson)
          ? ecomCatJson
          : [];
        setEcomCategories(ecomCats);

        const normalized = [
          ...ecomList.map((item) => ({
            ...item,
            type: 'ecommerce',
          })),
          ...corpList.map((item) => ({
            ...item,
            type: 'corporate',
          })),
          ...easyList.map((item) => ({
            ...item,
            type: 'easy',
          })),
        ];

        setTemplates(normalized);
      } catch (e) {
        console.error('WebDesignTemplatesPage fetch error:', e);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  // URL'den ?type= ve ?category= parametrelerini oku (ilk yüklemede filtreleri ayarla)
  useEffect(() => {
    const currentUrl = location.pathname + location.search;
    
    // Eğer bu URL zaten işlendiyse, tekrar işleme
    if (lastProcessedUrlRef.current === currentUrl) {
      return;
    }
    
    // URL parametrelerini işleme başladı olarak işaretle (flag'i sıfırla)
    setIsUrlParamsProcessed(false);
    
    const params = new URLSearchParams(location.search);
    const typeParam = (params.get('type') || '').toLowerCase();
    const categoryParamRaw = params.get('category') || params.get('cat') || '';
    const categoryParam = String(categoryParamRaw || '').trim();

    // Geçerli type ise uygula
    if (typeParam && TYPE_CONFIG[typeParam]) {
      setActiveType(typeParam);
    } else if (!typeParam) {
      // Type parametresi yoksa 'all' yap
      setActiveType('all');
    }

    // Kategori parametresini işle (kategoriler yüklendikten sonra)
    if (!categoryParam) {
      // Kategori parametresi yoksa, aktif kategoriyi sıfırla
      setActiveCategoryId(null);
      setIsUrlParamsProcessed(true);
      lastProcessedUrlRef.current = currentUrl;
      return;
    }

    // Kategoriler henüz yüklenmemişse bekle
    if (!Array.isArray(ecomCategories) || ecomCategories.length === 0) {
      return;
    }

    const categoryParamSlug = slugify(categoryParam);
    const categoryParamLower = categoryParam.toLowerCase();
    const categoryParamCompressed = categoryParamSlug.replace(/[aeiou]/g, '');

    // Önce legacy map ile bilinen üst kategori isimlerine eşlemeyi dene
    const legacyTargetName = LEGACY_CATEGORY_SLUG_MAP[categoryParamSlug] || 
                             LEGACY_CATEGORY_SLUG_MAP[categoryParamLower] ||
                             LEGACY_CATEGORY_SLUG_MAP[categoryParam];

    if (legacyTargetName) {
      const legacyMatch = ecomCategories.find((cat) => {
        const nameNorm = normalizeText(cat.name || '');
        const targetNorm = normalizeText(legacyTargetName);
        return nameNorm === targetNorm;
      });
      if (legacyMatch) {
        setActiveCategoryId(legacyMatch.id);
        setIsUrlParamsProcessed(true);
        lastProcessedUrlRef.current = currentUrl;
        return;
      }
    }

    // Normal eşleşme mantığı - daha kapsamlı kontrol
    const match = ecomCategories.find((cat) => {
      const idStr = String(cat.id);
      const slugStr = cat.slug ? String(cat.slug).toLowerCase() : '';
      const slugNorm = slugStr ? slugify(slugStr) : '';
      const nameSlug = slugify(cat.name || '');
      const nameNorm = normalizeText(cat.name || '');
      const slugCompressed = slugNorm.replace(/[aeiou]/g, '');
      const nameCompressed = nameSlug.replace(/[aeiou]/g, '');

      return (
        idStr === categoryParam ||
        slugStr === categoryParamLower ||
        slugNorm === categoryParamSlug ||
        nameSlug === categoryParamSlug ||
        nameNorm === normalizeText(categoryParam) ||
        // Daha toleranslı eşleşme: sesli harfleri yok sayarak kontrol et
        (!!categoryParamCompressed &&
          (slugCompressed === categoryParamCompressed ||
           nameCompressed === categoryParamCompressed))
      );
    });

    if (match) {
      setActiveCategoryId(match.id);
    } else {
      // Eşleşme bulunamazsa kategoriyi sıfırla
      setActiveCategoryId(null);
    }
    
    // URL parametreleri işlendi olarak işaretle
    setIsUrlParamsProcessed(true);
    lastProcessedUrlRef.current = currentUrl;
  }, [location.search, location.pathname, ecomCategories]);

  // Filtreler değiştiğinde URL'deki ?type= ve ?category= parametrelerini güncelle
  useEffect(() => {
    // URL parametreleri henüz işlenmediyse, URL'i güncelleme (ilk yüklemede URL'den okuma yapılırken)
    if (!isUrlParamsProcessed) {
      return;
    }

    // Kategoriler henüz yüklenmemişse bekle
    if (!Array.isArray(ecomCategories) || ecomCategories.length === 0) {
      return;
    }

    const currentParams = new URLSearchParams(location.search);
    const currentSearch = currentParams.toString();

    const params = new URLSearchParams();

    // type parametresi
    if (activeType && activeType !== 'all') {
      params.set('type', activeType);
    }

    // category parametresi
    if (activeCategoryId) {
      const cat = ecomCategories.find((c) => c.id === activeCategoryId);
      if (cat) {
        // Slug yoksa isme göre tutarlı bir slug üret
        const slugFromName = slugify(cat.name || '');
        const desiredCategory = String(cat.slug || slugFromName || cat.id);
        params.set('category', desiredCategory);
      }
    }

    const newSearch = params.toString();
    const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
    
    // Mevcut URL ile yeni URL aynıysa güncelleme yapma (sonsuz döngüyü önle)
    if (newSearch === currentSearch) {
      return;
    }

    // Eğer bu URL zaten işlenmişse, tekrar işleme
    if (lastProcessedUrlRef.current === newUrl) {
      return;
    }

    // URL'i güncelle (replace: true ile history'ye ekleme)
    lastProcessedUrlRef.current = newUrl;
    navigate(newUrl, { replace: true });
  }, [activeType, activeCategoryId, ecomCategories, location.pathname, navigate, isUrlParamsProcessed]);

  const filteredTemplates = useMemo(() => {
    let list = templates;

    // Kategori filtresi (şimdilik sadece e-ticaret tasarımlarına uygulanıyor)
    if (activeCategoryId) {
      const allowedIds = getCategoryAndDescendants(activeCategoryId);
      list = list.filter((tpl) => {
        if (tpl.type !== 'ecommerce') return true;
        const ids = Array.isArray(tpl.category_ids) ? tpl.category_ids : [];
        if (ids.length === 0 && tpl.category_id) {
          return allowedIds.includes(tpl.category_id);
        }
        return ids.some((id) => allowedIds.includes(id));
      });
    }

    if (activeType !== 'all') {
      if (activeType === 'professional_ecommerce') {
        // Profesyonel E-Ticaret: name, description veya tags içinde "profesyonel" veya "professional" geçen e-ticaret tasarımları
        list = list.filter((tpl) => {
          if (tpl.type !== 'ecommerce') return false;
          const searchText = normalizeText([
            tpl.name || '',
            tpl.description || '',
            tpl.short_description || '',
            tpl.long_description || '',
            Array.isArray(tpl.tags) ? tpl.tags.join(' ') : (typeof tpl.tags === 'string' ? tpl.tags : ''),
          ].join(' '));
          return searchText.includes('profesyonel') || searchText.includes('professional');
        });
      } else if (activeType === 'b2b') {
        // B2B: name, description veya tags içinde "b2b" geçen e-ticaret tasarımları
        list = list.filter((tpl) => {
          if (tpl.type !== 'ecommerce') return false;
          const searchText = normalizeText([
            tpl.name || '',
            tpl.description || '',
            tpl.short_description || '',
            tpl.long_description || '',
            Array.isArray(tpl.tags) ? tpl.tags.join(' ') : (typeof tpl.tags === 'string' ? tpl.tags : ''),
          ].join(' '));
          return searchText.includes('b2b');
        });
      } else {
        list = list.filter((tpl) => tpl.type === activeType);
      }
    }

    if (searchTerm.trim() !== '') {
      const termNorm = normalizeText(searchTerm.trim());
      list = list.filter((tpl) => {
        const baseTexts = [
          tpl.name,
          tpl.description,
          tpl.short_description,
          tpl.long_description,
        ].filter(Boolean);

        const categoryTexts = [];
        if (Array.isArray(tpl.category_names)) {
          categoryTexts.push(...tpl.category_names);
        }
        if (tpl.category) {
          categoryTexts.push(tpl.category);
        }

        const tagTexts = [];
        if (tpl.tags) {
          let tags = [];
          if (typeof tpl.tags === 'string') {
            try {
              tags = JSON.parse(tpl.tags);
            } catch {
              tags = tpl.tags.split(',').map((t) => t.trim());
            }
          } else if (Array.isArray(tpl.tags)) {
            tags = tpl.tags;
          }
          tagTexts.push(...tags);
        }

        const haystack = normalizeText(
          [...baseTexts, ...categoryTexts, ...tagTexts].join(' ')
        );

        // Elastik arama: içerme + kelime başı eşleşmesi
        if (haystack.includes(termNorm)) return true;
        const words = haystack.split(/\s+/);
        return words.some((w) => w.startsWith(termNorm));
      });
    }

    return list;
  }, [templates, activeType, searchTerm, activeCategoryId, ecomCategories]);

  // Arama dropdown'ı için öneriler (ilk 10 sonuç)
  const searchSuggestions = useMemo(() => {
    const term = searchTerm.trim();
    if (term.length < 2) return [];

    const termNorm = normalizeText(term);
    const matches = templates.filter((tpl) => {
      const baseTexts = [tpl.name, tpl.description].filter(Boolean);
      const categoryTexts = [];
      if (Array.isArray(tpl.category_names)) {
        categoryTexts.push(...tpl.category_names);
      }
      if (tpl.category) {
        categoryTexts.push(tpl.category);
      }
      const tagTexts = [];
      if (tpl.tags) {
        let tags = [];
        if (typeof tpl.tags === 'string') {
          try {
            tags = JSON.parse(tpl.tags);
          } catch {
            tags = tpl.tags.split(',').map((t) => t.trim());
          }
        } else if (Array.isArray(tpl.tags)) {
          tags = tpl.tags;
        }
        tagTexts.push(...tags);
      }
      const haystack = normalizeText(
        [...baseTexts, ...categoryTexts, ...tagTexts].join(' ')
      );
      if (haystack.includes(termNorm)) return true;
      const words = haystack.split(/\s+/);
      return words.some((w) => w.startsWith(termNorm));
    });

    return matches.slice(0, 10);
  }, [templates, searchTerm]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hover scroll CSS */}
      <style>{customStyles}</style>

      <main className="w-full px-0 sm:px-0 lg:px-0 py-0 sm:py-0 lg:py-0">
        {/* Hero + kategori + filtreler (mor arkaplan) */}
        <section className="w-full web-design-hero-section bg-gradient-to-br from-purple-800 via-purple-700 to-purple-600 text-white pb-10 sm:pb-12 lg:pb-14 mb-5">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-10 sm:pt-12 lg:pt-16">
            {/* Başlık + Arama */}
            <div className="text-center">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-3">
                Beğendiğiniz web sitesi tasarımını seçin
              </h1>
              <p className="text-sm sm:text-base text-purple-100 max-w-2xl mx-auto mb-6">
                E-ticaret, kurumsal ve kolay site tasarımlarını tek ekranda görün, projeniz için
                en uygun tasarımı saniyeler içinde keşfedin.
              </p>

              <div className="max-w-xl mx-auto">
                <div className="relative">
                  <Search className="w-4 h-4 sm:w-5 sm:h-5 absolute left-3 top-1/2 -translate-y-1/2 text-purple-300" />
                  <input
                    type="text"
                    placeholder="Tasarım Ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 sm:py-2.5 rounded-full border border-purple-300/70 bg-white text-sm sm:text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:border-yellow-300 text-gray-900 placeholder:text-purple-300"
                  />
                  {/* Arama öneri dropdown'ı */}
                  {searchSuggestions.length > 0 && (
                    <div className="absolute left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg max-h-72 overflow-y-auto z-30 text-left">
                      {searchSuggestions.map((tpl) => {
                        const cfg = TYPE_CONFIG[tpl.type] || TYPE_CONFIG.all;
                        const Icon = cfg.icon || LayoutGrid;
                        return (
                          <button
                            key={`${tpl.type}-${tpl.id || tpl.slug || tpl.name}`}
                            type="button"
                            onClick={() => {
                              setSearchTerm(tpl.name || '');
                              setActiveType('all');
                              setActiveCategoryId(null);
                            }}
                            className="w-full px-3 py-2 flex items-center justify-between gap-2 hover:bg-purple-50 text-sm"
                          >
                            <span className="flex-1 text-gray-800 text-left line-clamp-1">
                              {tpl.name}
                            </span>
                            <span className="flex items-center gap-1 text-[10px] text-gray-500">
                              <Icon className="w-3 h-3" />
                              {cfg.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              {/* Mobil: kategori & filtre açılır menü butonu */}
              <div className="mt-4 flex justify-center md:hidden">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="px-4 py-2 rounded-full border-white/60 bg-white/10 text-xs font-medium text-white hover:bg-white hover:text-purple-700 transition-all"
                  onClick={() => setIsMobileMenuOpen(true)}
                >
                  Kategorilere göz at →
                </Button>
              </div>
            </div>

            {/* Kategoriler + filtreler (ortalanmış) */}
            {categoryTree.length > 0 && (
              <div
                className="mt-8 sm:mt-10 hidden md:flex flex-col items-center gap-4"
                onMouseLeave={() => setHoveredRootId(null)}
              >
                {/* Üst seviye kategoriler */}
                <div className="flex flex-row flex-wrap justify-center gap-2">
                  {/* Tüm Tasarımlar butonu - kategori seçimini kaldırır */}
                  <button
                    type="button"
                    onClick={() => {
                      setActiveCategoryId(null);
                    }}
                    className={`text-sm sm:text-base px-3 py-1.5 rounded-full border transition-all ${
                      !activeCategoryId
                        ? 'bg-white text-purple-700 border-white shadow-sm'
                        : 'bg-purple-600/40 text-white border-purple-300/60 hover:bg-purple-500/80'
                    }`}
                  >
                    Tüm Tasarımlar
                  </button>
                  {categoryTree.map((root) => {
                    const isActiveRoot = activeRootId === root.id;
                    const isHovered = hoveredRootId === root.id;
                    return (
                      <div
                        key={root.id}
                        className="relative"
                        onMouseEnter={() => setHoveredRootId(root.id)}
                      >
                        <button
                          type="button"
                          onClick={() =>
                            setActiveCategoryId((prev) =>
                              prev === root.id ? null : root.id
                            )
                          }
                          className={`text-sm sm:text-base px-3 py-1.5 rounded-full border transition-all ${
                            isHovered || isActiveRoot
                              ? 'bg-white text-purple-700 border-white shadow-sm'
                              : 'bg-purple-600/40 text-white border-purple-300/60 hover:bg-purple-500/80'
                          }`}
                        >
                          {root.name}
                        </button>

                        {/* Dropdown: orta + alt kategoriler */}
                        {hoveredRootId === root.id && (
                          <div
                            className="absolute left-1/2 -translate-x-1/2 mt-2 bg-white text-gray-900 rounded-xl shadow-xl border border-gray-200 z-40 min-w-[320px] sm:min-w-[420px] lg:min-w-[560px] max-w-3xl p-4"
                            onMouseEnter={() => setHoveredRootId(root.id)}
                            onMouseLeave={() =>
                              setHoveredRootId((prev) => (prev === root.id ? null : prev))
                            }
                          >
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                              {root.children?.map((mid) => (
                                <div key={mid.id}>
                                  <div className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">
                                    {mid.name}
                                  </div>
                                  {mid.children && mid.children.length > 0 && (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                                      {mid.children.map((leaf) => (
                                        <button
                                          key={leaf.id}
                                          type="button"
                                          onClick={() =>
                                            setActiveCategoryId((prev) =>
                                              prev === leaf.id ? null : leaf.id
                                            )
                                          }
                                          className={`block w-full text-left text-sm ${
                                            activeCategoryId === leaf.id
                                              ? 'text-purple-700 font-semibold'
                                              : 'text-gray-600 hover:text-purple-600'
                                          }`}
                                        >
                                          {leaf.name}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Tip filtreleri */}
                <div className="flex flex-wrap items-center justify-center gap-2">
                  {Object.entries(TYPE_CONFIG).map(([key, cfg]) => {
                    const Icon = cfg.icon || LayoutGrid;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setActiveType(key)}
                        className={`inline-flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-sm sm:text-base font-medium border transition-all ${
                          activeType === key
                            ? 'bg-white text-purple-700 border-white shadow-sm'
                            : 'bg-purple-600/40 text-purple-50 border-purple-200/60 hover:bg-purple-500/80'
                        }`}
                      >
                        <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        <span>{cfg.label}</span>
                      </button>
                    );
                  })}
                </div>

                <div className="text-xs sm:text-sm text-purple-100">
                  {loading
                    ? 'Tasarımlar yükleniyor...'
                    : `${filteredTemplates.length} tasarım listeleniyor`}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Mobil sağdan açılan kategori & filtre menüsü */}
        {isMobileMenuOpen && (
          <div className="fixed inset-0 z-50 md:hidden">
            {/* Arka plan overlay */}
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            {/* Sağdan gelen panel */}
            <div className="absolute right-0 top-0 h-full w-full max-w-xs bg-white shadow-2xl flex flex-col">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                <h2 className="text-sm font-semibold text-gray-900">Filtreler & Kategoriler</h2>
                <button
                  type="button"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-1 rounded-full hover:bg-gray-100 text-gray-500"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-6">
                {/* Tip filtreleri */}
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Tasarım türü
                  </h3>
                  <div className="flex flex-col gap-2">
                    {Object.entries(TYPE_CONFIG).map(([key, cfg]) => {
                      const Icon = cfg.icon || LayoutGrid;
                      const isActive = activeType === key;
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => {
                            setActiveType(key);
                            setIsMobileMenuOpen(false);
                          }}
                          className={`w-full inline-flex items-center justify-between px-3 py-2 rounded-lg border text-sm ${
                            isActive
                              ? 'bg-purple-600 text-white border-purple-600'
                              : 'bg-white text-gray-800 border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <span className="flex items-center gap-2">
                            <Icon className="w-4 h-4" />
                            <span>{cfg.label}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Kategoriler */}
                {categoryTree.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      E-ticaret kategorileri
                    </h3>
                    <div className="space-y-2">
                      {/* Tüm Tasarımlar butonu - kategori seçimini kaldırır */}
                      <button
                        type="button"
                        onClick={() => {
                          setActiveCategoryId(null);
                          setIsMobileMenuOpen(false);
                        }}
                        className={`w-full px-3 py-2 rounded-lg border text-sm font-semibold ${
                          !activeCategoryId
                            ? 'bg-purple-600 text-white border-purple-600'
                            : 'bg-white text-gray-800 border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        Tüm Tasarımlar
                      </button>
                      {categoryTree.map((root) => {
                        const isExpanded = expandedMobileRootId === root.id;
                        return (
                          <div key={root.id} className="border border-gray-100 rounded-lg">
                            <button
                              type="button"
                              onClick={() =>
                                setExpandedMobileRootId((prev) =>
                                  prev === root.id ? null : root.id
                                )
                              }
                              className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-gray-900"
                            >
                              <span>{root.name}</span>
                              <span className="text-xs text-gray-400">
                                {isExpanded ? '−' : '+'}
                              </span>
                            </button>

                            {isExpanded && root.children && root.children.length > 0 && (
                              <div className="pb-2 space-y-1 px-3 border-t border-gray-100">
                                {root.children.map((mid) => (
                                  <div key={mid.id} className="mb-1">
                                    <div className="text-xs font-medium text-gray-700 mb-0.5">
                                      {mid.name}
                                    </div>
                                    {mid.children && mid.children.length > 0 && (
                                      <div className="flex flex-wrap gap-1">
                                        {mid.children.map((leaf) => {
                                          const isActive = activeCategoryId === leaf.id;
                                          return (
                                            <button
                                              key={leaf.id}
                                              type="button"
                                              onClick={() => {
                                                setActiveCategoryId((prev) =>
                                                  prev === leaf.id ? null : leaf.id
                                                );
                                                setIsMobileMenuOpen(false);
                                              }}
                                              className={`px-2 py-1 rounded-full text-[11px] border ${
                                                isActive
                                                  ? 'bg-purple-600 text-white border-purple-600'
                                                  : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                                              }`}
                                            >
                                              {leaf.name}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {!loading && (
                <div className="px-4 py-3 border-t border-gray-200 text-xs text-gray-500">
                  {`${filteredTemplates.length} tasarım listeleniyor`}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mb-3" />
              <p className="text-gray-600 text-sm sm:text-base">Tasarım listesi hazırlanıyor...</p>
            </div>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-base sm:text-lg font-semibold text-gray-800 mb-2">
              Eşleşen tasarım bulunamadı
            </p>
            <p className="text-sm text-gray-500">
              Farklı bir arama terimi deneyebilir veya filtreleri sıfırlayabilirsiniz.
            </p>
          </div>
        ) : (
          <div className="grid gap-5 sm:gap-6 lg:gap-7 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {filteredTemplates.map((tpl) => (
              <TemplateCard key={`${tpl.type}-${tpl.id || tpl.slug || tpl.name}`} template={tpl} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

